import { formatFromAPI } from '@eb/datetime';
import {
    EVENT_PRIVACY_ACCESS_INVITE,
    EVENT_PRIVACY_ACCESS_LINK,
    EVENT_PRIVACY_ACCESS_PASSWORD,
    LISTED_PRIVATE,
    LISTED_PUBLIC,
    SCHEDULE_PRIVACY_PENDING,
} from '../constants';

export const mapSchedulePrivacySettingsFromApi = (
    { event_privacy_schedules }: { event_privacy_schedules: Array<any> },
    timezone: string,
) => {
    const pendingFound = event_privacy_schedules?.find(
        ({ status }: { status: string }) => status === SCHEDULE_PRIVACY_PENDING,
    );

    if (pendingFound) {
        const { id, schedule_date } = pendingFound;

        const formattedDate = formatFromAPI(schedule_date, timezone);

        return {
            id,
            formattedDate,
        };
    }

    return null;
};

export const mapPrivacyValuesFromAPI = ({
    listed,
    inviteOnly,
    password,
}: {
    listed: boolean;
    inviteOnly: boolean;
    password?: string;
}) => {
    let eventPrivacyAccess = EVENT_PRIVACY_ACCESS_LINK;

    if (!listed && inviteOnly) {
        eventPrivacyAccess = EVENT_PRIVACY_ACCESS_INVITE;
    } else if (!listed && password) {
        eventPrivacyAccess = EVENT_PRIVACY_ACCESS_PASSWORD;
    }

    return {
        eventListing: listed ? LISTED_PUBLIC : LISTED_PRIVATE,
        eventPrivacyAccess,
        eventPassword: password,
    };
};

/**
 * mapPrivacyConfigToAPI - Transforms the current state of the PrivacySettings component
 * into data formatted for the API.
 *
 * @param eventListing, {String} - Event availability ( public or private )
 * @param eventPrivacyAccess, {String} - Privacy status of the event
 * @param eventPassword, {String} - Password to access event
 *
 * @return {Object}, API data for event privacy settings
 */
export const mapPrivacyConfigToAPI = ({
    eventListing,
    eventPrivacyAccess,
    eventPassword,
}: {
    eventListing: string;
    eventPrivacyAccess: string;
    eventPassword?: string;
}) => {
    const listed = eventListing === LISTED_PUBLIC;
    const inviteOnly =
        !listed && eventPrivacyAccess === EVENT_PRIVACY_ACCESS_INVITE;
    const passwordProtected =
        !listed && eventPrivacyAccess === EVENT_PRIVACY_ACCESS_PASSWORD;
    const password = passwordProtected ? eventPassword : '';

    return {
        event: {
            listed,
            shareable: listed,
            invite_only: inviteOnly,
            password,
        },
    };
};

/**
 * getPrivacyAccessFromAPI - Maps an event's privacy settings data to
 * a formatted to props for the privacy settings component.
 *
 * @param listed, {Boolean} - Is event public?
 * @param inviteOnly, {Boolean} - Is the event inviteOnly?
 * @param eventPassword, {String} - Password to access event
 *
 * @return {Object}, Component props
 */
export const getPrivacyAccessFromAPI = ({
    listed,
    invite_only: inviteOnly,
    password,
}: {
    listed: boolean;
    invite_only: boolean;
    password?: string;
}) => {
    const eventListing = listed ? LISTED_PUBLIC : LISTED_PRIVATE;
    let eventPrivacyAccess = EVENT_PRIVACY_ACCESS_LINK;
    const eventPassword = password;

    if (!listed && inviteOnly) {
        eventPrivacyAccess = EVENT_PRIVACY_ACCESS_INVITE;
    } else if (!listed && eventPassword) {
        eventPrivacyAccess = EVENT_PRIVACY_ACCESS_PASSWORD;
    }

    return {
        eventListing,
        eventPrivacyAccess,
        eventPassword,
    };
};
